import { z } from 'zod';

const StructuredTechniqueStateChangeSchema = z
  .object({
    attempted: z.boolean().optional(),
    successful: z.boolean().optional(),
    attribute: z.string().optional(),
  })
  .passthrough();

const StructuredTechniqueGrantingAceSchema = z
  .object({
    trustee_sid: z.string().optional(),
    right_name: z.string().optional(),
    ace_type: z.string().optional(),
    is_inherited: z.boolean().optional(),
    access_mask: z.number().optional(),
  })
  .passthrough();

const StructuredTechniquePayloadSchema = z
  .object({
    summary: z.string().optional(),
    abuse_action: z.string().optional(),
    abuse_possible: z.boolean().optional(),
    state_change: StructuredTechniqueStateChangeSchema.optional(),
    state_change_successful: z.boolean().optional(),
    verification_successful: z.boolean().optional(),
    success: z.boolean().optional(),
    granting_aces: z.array(StructuredTechniqueGrantingAceSchema).optional(),
    rights_granted: z.array(z.string()).optional(),
    rights: z
      .object({
        checked: z.array(z.string()).optional(),
        granted: z.array(z.string()).optional(),
        required: z.array(z.string()).optional(),
      })
      .partial()
      .optional(),
    target_sam: z.string().optional(),
    acquired_password: z.string().optional(),
  })
  .passthrough();

export type StructuredTechniquePayload = z.infer<
  typeof StructuredTechniquePayloadSchema
>;

const NestedStdoutEnvelopeSchema = z.object({
  stdout: z.string().optional(),
  output: z.string().optional(),
});

const unwrapTechniquePayload = ({ output }: { output: string }): unknown => {
  let parsed: unknown;

  try {
    parsed = JSON.parse(output);
  } catch {
    return null;
  }

  const envelope = NestedStdoutEnvelopeSchema.safeParse(parsed);

  if (envelope.success && typeof envelope.data.stdout === 'string') {
    try {
      return JSON.parse(envelope.data.stdout);
    } catch {
      return parsed;
    }
  }

  return parsed;
};

export const parseStructuredTechniquePayload = ({
  output,
}: {
  output: string;
}): StructuredTechniquePayload | null => {
  const result = StructuredTechniquePayloadSchema.safeParse(
    unwrapTechniquePayload({ output }),
  );

  return result.success ? result.data : null;
};

export const grantedTechniqueRights = (
  payload: StructuredTechniquePayload,
): string[] => payload.rights_granted ?? payload.rights?.granted ?? [];

export const ACL_PROBE_NO_EFFECTIVE_RIGHTS_SUMMARY =
  'acl_probe found no effective rights granted to the acting principal';

export const hasSuccessfulTechniqueStateChange = (
  payload: StructuredTechniquePayload,
): boolean =>
  payload.state_change_successful === true ||
  payload.state_change?.successful === true;

export const hasEffectiveAclProbeRights = ({
  output,
  failureReason,
}: {
  output?: string | null;
  failureReason?: string | null;
}): boolean => {
  if (
    typeof failureReason === 'string' &&
    failureReason.includes(ACL_PROBE_NO_EFFECTIVE_RIGHTS_SUMMARY)
  ) {
    return false;
  }

  if (!output) {
    return false;
  }

  const payload = parseStructuredTechniquePayload({ output });

  if (!payload) {
    return false;
  }

  return (
    grantedTechniqueRights(payload).length > 0 ||
    (payload.granting_aces ?? []).length > 0
  );
};

export const isConfirmedEmptyAclProbe = ({
  output,
  failureReason,
}: {
  output?: string | null;
  failureReason?: string | null;
}): boolean => {
  if (
    typeof failureReason === 'string' &&
    failureReason.includes(ACL_PROBE_NO_EFFECTIVE_RIGHTS_SUMMARY)
  ) {
    return true;
  }

  if (!output) {
    return false;
  }

  const payload = parseStructuredTechniquePayload({ output });

  if (!payload) {
    return false;
  }

  const hasRightsField =
    payload.rights_granted !== undefined ||
    payload.rights?.granted !== undefined ||
    payload.granting_aces !== undefined;

  if (!hasRightsField) {
    return false;
  }

  return (
    grantedTechniqueRights(payload).length === 0 &&
    (payload.granting_aces ?? []).length === 0
  );
};

export const isAclAbuseTechniqueSuccess = ({
  output,
  failureReason,
}: {
  output?: string | null;
  failureReason?: string | null;
}): boolean => {
  if (typeof failureReason === 'string' && failureReason.trim().length > 0) {
    return false;
  }

  if (!output) {
    return false;
  }

  const payload = parseStructuredTechniquePayload({ output });

  if (!payload) {
    return false;
  }

  return (
    payload.success !== false &&
    payload.verification_successful !== false &&
    (payload.abuse_possible === true ||
      hasSuccessfulTechniqueStateChange(payload))
  );
};
